import React, {useState} from "react";
import {Box, CssBaseline, Grid, ThemeProvider, useMediaQuery} from "@mui/material";
import {BrowserRouter as Router, Routes, Route} from 'react-router-dom';
import {GlobalStateProvider} from "./state/GlobalStateProvider";
import theme from './common/Theme';
import ReservationManagement from "./components/management/ReservationManagement";
import AppointmentsManagement from "./components/management/AppointmentsManagement";
import NewReservationsOverview from "./components/management/NewReservationsOverview";
import NewReservation from "./flowGebruiker/NewReservation";
import PersonForm from "./flowGebruiker/PersonForm";
import CatInformationForm from "./flowGebruiker/AnimalInformationForm";
import MyReservations from "./flowGebruiker/MyReservations";
import VeterinaryForm from "./flowGebruiker/VeterinaryForm";
import ProtectedRoute from "./components/security/ProtectedRoute";
import PaymentProcessor from "./components/user/PaymentProcessor";
import Login from "./components/common/Login";
import EmergencyContactForm from "./flowGebruiker/EmergencyContact";
import './i18n';
import Vacations from "./components/management/Vacations";
import AddReservation from "./components/management/AddReservation";
import AddAppointment from "./components/management/AddAppointment";
import _GebruikerMain from "./flowGebruiker/_GebruikerMain";
import _BeheerMain from "./flowBeheer/_BeheerMain";
import Customers from "./flowBeheer/Customers";
import Animals from "./flowBeheer/Animals";
import AnimalDetail from "./flowBeheer/AnimalDetail";
import CustomerDetail from "./flowBeheer/CustomerDetail";
import ReservationsDashboard from "./flowBeheer/ReservationsDashboard";
import AppointmentDashboard from "./flowBeheer/dashboard/AppointmentsDashboard";
import {ApolloAuthProvider} from "./security/ApolloAuthProvider";
import Configuratie from "./flowBeheer/Configuration";
import EmailTemplateEditor from "./flowBeheer/EmailTemplateEditor";
import Unauthorized from "./flowGebruiker/Unauthorized";
import {useTheme} from "@mui/material";
import {Navigate} from "react-router-dom";
import {useTenantConfig} from "./security/tenant-config-context";

function App() {
    const [drawerOpen, setDrawerOpen] = useState(true);
    const [collapsed, setCollapsed] = useState(false)

    const reservationUuid = '';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const effectiveDrawerWidth = isMobile ? 0 : (collapsed ? 10 : 200);
    const { cfg } = useTenantConfig();
    const isHotelFlow = cfg?.businessTypes?.includes('HOTEL_BASED');

    function HideOnXsRoute({children, redirectTo = "/beheer"}) {
        const theme = useTheme();
        const isXs = useMediaQuery(theme.breakpoints.down("sm"));
        if (isXs) return <Navigate to={redirectTo} replace/>;
        return children;
    }

    return (
        <div className="App">
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <Router>
                        <ApolloAuthProvider>
                            <Box sx={{display: 'flex'}}>
                                <GlobalStateProvider>
                                    <Box
                                        component="main"
                                        sx={{
                                            flexGrow: 1,
                                            p: 3,
                                            transition: 'margin-left 0.3s ease, width 0.3s ease',
                                            marginLeft: `${effectiveDrawerWidth}px`,
                                            width: `calc(100% - ${effectiveDrawerWidth}px)`,
                                            overflowX: 'hidden'
                                        }}
                                    >
                                        <Grid container spacing={2} sx={{mt: 2}}> {/* Adjust the margin-top (mt) as needed */}
                                            <Grid item xs={12}>
                                                <Routes>
                                                    {/* eslint-disable-next-line react/jsx-pascal-case */}
                                                    <Route path="/" element={<_GebruikerMain/>}>
                                                        <Route index element={<NewReservation/>}/>
                                                        <Route path="login" element={<Login/>}/>
                                                        <Route path="mijn-reservaties" element={<MyReservations/>}/>
                                                        <Route path="mijn-afspraken" element={<MyReservations/>}/>
                                                        <Route path="nieuwe-reservatie" element={<NewReservation/>}/>
                                                        {/*<Route path="/nieuwe-reservatie-planner" element={<NewReservationPlanner/>}/>*/}
                                                        <Route path="mijn-dierenarts" element={<VeterinaryForm/>}/>
                                                        <Route path="mijn-profiel" element={<PersonForm reservationUuid={reservationUuid}/>}/>
                                                        <Route path="noodcontact" element={<EmergencyContactForm reservationUuid={reservationUuid}/>}/>
                                                        <Route path="mijn-katten" element={<CatInformationForm reservationUuid={reservationUuid}/>}/>
                                                        <Route path="payment-processor" element={<PaymentProcessor/>}/>
                                                        <Route path="unauthorized" element={<Unauthorized/>}/>
                                                    </Route>
                                                    {/* eslint-disable-next-line react/jsx-pascal-case */}
                                                    <Route path="/beheer" element={<_BeheerMain
                                                        drawerOpen={drawerOpen}
                                                        setDrawerOpen={setDrawerOpen}
                                                        collapsed={collapsed}
                                                        setCollapsed={setCollapsed}
                                                    />}>
                                                        <Route path="login" element={<Login/>}/>
                                                        <Route index element={
                                                            <ProtectedRoute roles={['cat-admin']} element={isHotelFlow ? <ReservationsDashboard/> : <AppointmentDashboard/>}/>
                                                        }/>
                                                        <Route path="dashboard" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={isHotelFlow ? <ReservationsDashboard/> : <AppointmentDashboard/>}/>
                                                        }/>
                                                        <Route path="reservatie-beheer" element={
                                                            <HideOnXsRoute redirectTo="/beheer">
                                                                <ProtectedRoute roles={['cat-admin']} element={<ReservationManagement/>}/>
                                                            </HideOnXsRoute>
                                                        }/>
                                                        <Route path="afspraak-beheer" element={
                                                            <HideOnXsRoute redirectTo="/beheer">
                                                                <ProtectedRoute roles={['cat-admin']} element={<AppointmentsManagement/>}/>
                                                            </HideOnXsRoute>
                                                        }/>
                                                        <Route path="dieren" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={<Animals/>}/>
                                                        }/>
                                                        <Route path="dieren/:uuid" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={<AnimalDetail/>}/>
                                                        }/>
                                                        <Route path="klanten" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={<Customers/>}/>
                                                        }/>
                                                        <Route path="klanten/:uuid" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={<CustomerDetail/>}/>
                                                        }/>
                                                        <Route path="vacation" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={<Vacations/>}/>
                                                        }/>
                                                        <Route path="reservatie-overzicht" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={<NewReservationsOverview/>}/>
                                                        }/>
                                                        <Route path="afspraak-overzicht" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={<NewReservationsOverview/>}/>
                                                        }/>
                                                        <Route path="reservatie-toevoegen" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={<AddReservation/>}/>
                                                        }/>
                                                        <Route path="afspraak-toevoegen" element={
                                                            <ProtectedRoute roles={['cat-admin']} element={<AddAppointment/>}/>
                                                        }/>
                                                        <Route path="configuratie" element={
                                                            <HideOnXsRoute redirectTo="/beheer">
                                                                <ProtectedRoute roles={['cat-admin']} element={<Configuratie/>}/>
                                                            </HideOnXsRoute>
                                                        }/>
                                                        <Route path="configuratie/emails/:id" element={
                                                            <HideOnXsRoute redirectTo="/beheer">
                                                                <ProtectedRoute roles={['cat-admin']} element={<EmailTemplateEditor/>}/>
                                                            </HideOnXsRoute>
                                                        }/>
                                                        <Route path="configuratie/emails/new" element={
                                                            <HideOnXsRoute redirectTo="/beheer">
                                                                <ProtectedRoute roles={['cat-admin']} element={<EmailTemplateEditor/>}/>
                                                            </HideOnXsRoute>
                                                        }/>
                                                    </Route>
                                                </Routes>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </GlobalStateProvider>
                            </Box>
                        </ApolloAuthProvider>
                </Router>
            </ThemeProvider>
        </div>
    );
}

export default App;
